import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  IconButton,
  Tooltip,
  Chip,
  Button,
} from '@mui/material';
import { Visibility as ViewIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { memoryService } from '../services/memoryService';
import { useNotificationStore } from '../store/notificationStore';
import { MemoryScope, MemoryType, type AgentMemoryEntryDto, type MemoryFilters } from '../types/memory';
import { TextField } from '../@core/components/mui';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { PageListLayout } from '../components/layouts/PageListLayout';

const scopeLabel = (scope: number) => {
  switch (scope) {
    case MemoryScope.Session:
      return 'Session';
    case MemoryScope.Project:
      return 'Project';
    case MemoryScope.Tenant:
      return 'Tenant';
    default:
      return 'Unknown';
  }
};

const typeLabel = (type: number) => {
  switch (type) {
    case MemoryType.Fact:
      return 'Fact';
    case MemoryType.Decision:
      return 'Decision';
    case MemoryType.Outcome:
      return 'Outcome';
    case MemoryType.Preference:
      return 'Preference';
    default:
      return 'Unknown';
  }
};

/**
 * Memory list page component.
 */
export const MemoryListPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addNotification } = useNotificationStore();

  const initialSessionId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('sessionId') || undefined;
  }, [location.search]);

  const [memories, setMemories] = useState<AgentMemoryEntryDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<MemoryFilters>({
    sessionId: initialSessionId,
    query: '',
    scope: undefined,
    type: undefined,
    minImportanceScore: 0,
    useSemanticSearch: true,
    limit: 50,
    includeProjectScope: true,
    includeTenantScope: true,
  });

  const loadMemories = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await memoryService.getMemories(filters);
      setMemories(result);
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to load memories';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMemories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.sessionId,
    filters.query,
    filters.scope,
    filters.type,
    filters.minImportanceScore,
    filters.useSemanticSearch,
    filters.limit,
    filters.includeProjectScope,
    filters.includeTenantScope,
  ]);

  const handleDelete = async (m: AgentMemoryEntryDto) => {
    if (!window.confirm('Delete this memory entry?')) return;
    try {
      await memoryService.deleteMemory(m.id);
      addNotification('Memory deleted', 'success');
      await loadMemories();
    } catch (err: any) {
      const msg = err.response?.data?.message || 'Failed to delete memory';
      addNotification(msg, 'error');
    }
  };

  // Define table columns
  const tableColumns: DataTableColumn<AgentMemoryEntryDto>[] = [
    {
      id: 'scope',
      label: 'Scope',
      minWidth: 100,
      render: (memory) => scopeLabel(memory.scope),
    },
    {
      id: 'type',
      label: 'Type',
      minWidth: 100,
      render: (memory) => typeLabel(memory.type),
    },
    {
      id: 'summary',
      label: 'Summary',
      minWidth: 300,
      render: (memory) => (
        <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
          {memory.summary || (memory.content.length > 120 ? `${memory.content.slice(0, 120)}…` : memory.content)}
        </Typography>
      ),
    },
    {
      id: 'importanceScore',
      label: 'Importance',
      minWidth: 100,
      align: 'right',
      render: (memory) => memory.importanceScore.toFixed(2),
    },
    {
      id: 'tags',
      label: 'Tags',
      minWidth: 150,
      render: (memory) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {memory.tags?.slice(0, 3).map((t) => (
            <Chip key={`${memory.id}-${t}`} label={t} size="small" />
          ))}
          {memory.tags && memory.tags.length > 3 && (
            <Chip label={`+${memory.tags.length - 3}`} size="small" variant="outlined" />
          )}
        </Box>
      ),
    },
    {
      id: 'createdAt',
      label: 'Created',
      minWidth: 180,
      render: (memory) => new Date(memory.createdAt).toLocaleString(),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 120,
      align: 'right',
      render: (memory) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title="View">
            <IconButton onClick={() => navigate(`/memories/${memory.id}`)} size="small">
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton onClick={() => handleDelete(memory)} size="small" color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <PageListLayout
      title="Memories"
      headerActions={
        filters.sessionId ? (
          <Button variant="outlined" onClick={() => navigate(`/sessions/${filters.sessionId}`)}>
            Back to session
          </Button>
        ) : undefined
      }
      filters={
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
          <TextField
            label="Search"
            size="small"
            value={filters.query || ''}
            onChange={(e) => setFilters((p) => ({ ...p, query: e.target.value, limit: p.limit }))}
            sx={{ minWidth: 280, flexGrow: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Scope</InputLabel>
            <Select
              label="Scope"
              value={filters.scope ?? ''}
              onChange={(e) => {
                const value = e.target.value as unknown as string | number;
                setFilters((p) => ({
                  ...p,
                  scope: value === '' ? undefined : (Number(value) as any),
                }));
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value={MemoryScope.Session}>Session</MenuItem>
              <MenuItem value={MemoryScope.Project}>Project</MenuItem>
              <MenuItem value={MemoryScope.Tenant}>Tenant</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={filters.type ?? ''}
              onChange={(e) => {
                const value = e.target.value as unknown as string | number;
                setFilters((p) => ({
                  ...p,
                  type: value === '' ? undefined : (Number(value) as any),
                }));
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value={MemoryType.Fact}>Fact</MenuItem>
              <MenuItem value={MemoryType.Decision}>Decision</MenuItem>
              <MenuItem value={MemoryType.Outcome}>Outcome</MenuItem>
              <MenuItem value={MemoryType.Preference}>Preference</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ minWidth: 260 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Min importance: {(filters.minImportanceScore ?? 0).toFixed(2)}
            </Typography>
            <Slider
              value={filters.minImportanceScore ?? 0}
              min={0}
              max={1}
              step={0.05}
              onChange={(_, value) => setFilters((p) => ({ ...p, minImportanceScore: value as number }))}
            />
          </Box>
        </Box>
      }
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      isEmpty={!loading && memories.length === 0}
      emptyState={{
        title: 'No memories found',
        description: 'No memory entries match your current filters.',
      }}
    >
      <DataTable
        columns={tableColumns}
        rows={memories}
      />
    </PageListLayout>
  );
};

